/**
 * "Where the unattributed sit" (L1): the slow interactions with no Cato row,
 * ranked by the Entra country × office of the person — the only location an
 * unattributed row has. A horizontal bar (a ranking, §2), painted in the
 * unattributed family's neutral colour so it reads as the same slice the
 * donut shows, never as a finding.
 *
 * Why it exists: 1,148 of the first day's slow interactions were unattributed,
 * and the honest question is not "which site" (there is none) but "which
 * offices are outside Cato's view, and how many people there are waiting".
 * That is what decides where the Identity Agent or the browser extension goes
 * next.
 *
 * Four states, kept apart: the payload predates contract v3 (no field), the
 * origins query did not answer (`null` — unknown, not zero), the field is
 * there and empty (nothing unattributed), the field has rows.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState, Typography } from "@norican/react";
import { useMemo } from "react";

import { ChartCard, TABLE_TWIN_PARAM } from "@/components/board";
import type {
  FoConsoleLookback,
  FoConsoleSlowSummary,
  FoConsoleUnattributedOrigin,
} from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { classificationColor } from "./console-chips";
import {
  formatCoverage,
  formatOrigin,
  lookbackOption,
  summaryUnattributedOrigins,
} from "./console-format";
import { formatCount, tooltipValue } from "./status";

/** Bars on the chart; the rest fold into one stated "Other" bar. */
const BAR_LIMIT = 8;

const TWIN_ID = "fo-unattributed-origins";

const ORIGIN_CONFIG = {
  count: { label: "Unattributed slow interactions", color: classificationColor("UNATTRIBUTED") },
};

interface OriginRow {
  key: string;
  label: string;
  count: number;
  /** `null` when the backend withheld the user count (k-anonymity). */
  users: number | null;
  /** A fold, not a place: the backend's k-anonymity `Other` row, or this card's tail past the bar limit. */
  isOther: boolean;
  /** How many labelled origins this card folded into its own `Other` bar. */
  folded: number;
}

interface UnattributedOriginsCardProps {
  summary: FoConsoleSlowSummary;
  lookback: FoConsoleLookback;
}

/**
 * Labelled origins first, by count; the backend's k-anonymity fold (`is_other`)
 * last, named as a fold so it never reads as a country.
 */
function toRows(origins: FoConsoleUnattributedOrigin[]): OriginRow[] {
  const labelled = origins
    .filter((origin) => !origin.is_other)
    .sort((a, b) => b.count - a.count || (b.users ?? 0) - (a.users ?? 0))
    .map<OriginRow>((origin) => ({
      key: `${origin.country}|${origin.office_location}`,
      label: formatOrigin({ country: origin.country, office: origin.office_location }),
      count: origin.count,
      users: origin.users,
      isOther: false,
      folded: 0,
    }));
  const backendOther = origins
    .filter((origin) => origin.is_other)
    .map<OriginRow>((origin, index) => ({
      key: `backend-other-${index}`,
      label: "Other (folded cohorts)",
      count: origin.count,
      users: origin.users,
      isOther: true,
      folded: 0,
    }));
  return [...labelled, ...backendOther];
}

export function UnattributedOriginsCard({ summary, lookback }: UnattributedOriginsCardProps) {
  const twin = useDrawerParam(TABLE_TWIN_PARAM);
  const origins = summaryUnattributedOrigins(summary);
  const windowCaption = lookbackOption(lookback).caption;
  const coverageText = formatCoverage(summary.coverage);

  const rows = useMemo(() => toRows(origins ?? []), [origins]);
  const unattributedTotal = rows.reduce((sum, row) => sum + row.count, 0);

  // At most BAR_LIMIT labelled bars; the rest fold into one stated "Other" bar
  // together with the backend's own k-anonymity fold, so there is one fold, not two.
  const barRows = useMemo<OriginRow[]>(() => {
    const labelled = rows.filter((row) => !row.isOther);
    const folds = rows.filter((row) => row.isOther);
    if (labelled.length <= BAR_LIMIT && folds.length <= 1) {
      return rows;
    }
    const head = labelled.slice(0, BAR_LIMIT);
    const tail = [...labelled.slice(BAR_LIMIT), ...folds];
    const usersKnown = tail.every((row) => row.users != null);
    return [
      ...head,
      {
        key: "other",
        label: `Other (${formatCount(tail.length)} origins)`,
        count: tail.reduce((sum, row) => sum + row.count, 0),
        users: usersKnown ? tail.reduce((sum, row) => sum + (row.users ?? 0), 0) : null,
        isOther: true,
        folded: tail.length,
      },
    ];
  }, [rows]);

  const caption =
    origins == null
      ? `${windowCaption} · network coverage ${coverageText} · origins ${origins === null ? "not answered" : "not in this payload"}`
      : `${formatCount(unattributedTotal)} unattributed of ${formatCount(summary.total)} slow, ${windowCaption} · by Entra country · office · network coverage ${coverageText}`;

  return (
    <ChartCard<OriginRow>
      title="Where the unattributed sit"
      caption={caption}
      since={summary.collected_at}
      staleAfterMs={summary.cache_ttl_sec * 2000}
      tableTwin={{
        id: TWIN_ID,
        ariaLabel: "Unattributed slow interactions per Entra country and office",
        data: rows,
        getRowId: (row) => row.key,
        columns: [
          { id: "origin", header: "Country · office", accessor: (row) => row.label },
          {
            id: "count",
            header: "Slow",
            align: "end",
            accessor: (row) => row.count,
            cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
          },
          {
            id: "users",
            header: "Users",
            align: "end",
            accessor: (row) => row.users,
            cell: (row) => <span className="tabular-nums">{formatCount(row.users)}</span>,
          },
        ],
      }}
    >
      {origins === undefined ? (
        <EmptyState
          size="sm"
          title="Origins not in this payload"
          description="The slow-summary payload carries no unattributed_origins field: the console backend is older than this page. Nothing here is zero — it is unreported."
        />
      ) : origins === null ? (
        <EmptyState
          size="sm"
          title="Origins not answered"
          description={`The unattributed-origins query did not answer this time (see the errors above); the rest of the summary is live. Where the unattributed sit is unknown for the ${windowCaption} — not zero, not "every slow interaction had a Cato row".`}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          size="sm"
          title="No unattributed slow interaction"
          description={`Every slow interaction in the ${windowCaption} had a Cato row. Coverage ${coverageText}.`}
        />
      ) : (
        <div className="flex flex-col gap-2">
          <Chart.Container
            config={ORIGIN_CONFIG}
            className="h-64"
            aria-label={`Unattributed slow interactions ranked by Entra country and office, ${windowCaption}`}
          >
            <Chart.BarChart
              data={barRows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="label" width={150} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    rows={(datum: OriginRow) => [
                      { label: "Slow", value: formatCount(tooltipValue(datum.count)) },
                      { label: "Users", value: formatCount(datum.users) },
                    ]}
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="count"
                fill="var(--color-count)"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={() => twin.open(TWIN_ID)}
              />
            </Chart.BarChart>
          </Chart.Container>
          <Typography variant="caption" tone="muted">
            Country and office come from Entra (FoUserMap), not from the network: these people sit
            where Cato has no Identity Agent or no breakout. A bar is a rollout target, not a
            verdict.
            {barRows.some((row) => row.isOther) &&
              ` ${formatCount(barRows.find((row) => row.isOther)?.folded ?? 0)} smaller origins are folded into Other — View data lists them.`}
          </Typography>
        </div>
      )}
    </ChartCard>
  );
}
